import { formatMoney } from 'betterplace-utils'
import { BarController, BarElement, CategoryScale, Chart, LinearScale, PointElement, Title, Tooltip } from 'chart.js'
import classNames from 'classnames'
import { createBrowserHistory } from 'history'
import { decode } from 'html-entities'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DetailedChartProps, Dimension, DimensionToggleButtonsProps, ProjectList } from './types'
import { JSONFetcher, QueryHelper, themeColor } from 'src/shared'

Chart.register(BarElement, PointElement, BarController, CategoryScale, LinearScale, Title, Tooltip)
Chart.defaults.elements.bar.backgroundColor = themeColor('purple-600')
Chart.defaults.elements.point.backgroundColor = themeColor('purple-600')
Chart.defaults.elements.point.radius = 5

const Dimensions: Dimension[] = ['sum', 'count', 'avg']
export const DetailedChart = ({ apiUrl, filters, firstYear, projects: projects_ }: DetailedChartProps): JSX.Element => {
  const [dimension, setDimension] = useState<Dimension>('sum')
  const [period, setPeriod] = useState('')
  const params = QueryHelper.parseQuery()
  const [activeProjectId, setActiveProjectId] = useState(params?.filter)
  const [response, setResponse] = useState<Chart['data'] | undefined>(undefined)
  const canvasRef: React.MutableRefObject<HTMLCanvasElement | null> = useRef(null)
  const [chart, setChart] = useState<Chart | undefined>(undefined)
  const startYear = useMemo(() => parseInt(firstYear), [firstYear])
  const years = useMemo(
    () =>
      Array(new Date().getFullYear() - startYear + 1)
        .fill(undefined)
        .map((_, idx) => startYear + idx),
    [startYear]
  )
  const projects = useMemo(() => JSON.parse(projects_) as ProjectList, [projects_])
  const projects_filter_selection_items = projects.filter((p) => p.state !== 'closed')
  const projects_filter_selection_secondary_items = projects.filter((p) => p.state === 'closed')

  useEffect(() => {
    if (!canvasRef.current) return
    setChart(
      new Chart(canvasRef.current, {
        type: 'bar',
        data: {
          labels: [],
          datasets: [
            {
              data: [],
              maxBarThickness: 80,
            },
          ],
        },
        options: {
          aspectRatio: 1.92,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              displayColors: false,
              backgroundColor: 'white',
              titleColor: 'black',
              bodyColor: 'black',
              borderWidth: 1,
              borderColor: themeColor('gray-300'),
              padding: 12,
              cornerRadius: 4,
            },
          },
          scales: {
            y: {
              min: 0,
              ticks: {
                precision: 0,
                padding: 8,
                color: 'black',
                // initial dimension uses money formatted labels
                callback: (value) => formatMoney(i18n.locale, parseFloat(`${value}`), { precision: 0 }),
              },
              grid: {
                drawTicks: false,
              },
            },

            x: {
              ticks: {
                padding: 8,
                color: 'black',
                font: {
                  weight: 'bold',
                },
              },
              grid: {
                drawTicks: false,
                drawOnChartArea: false,
              },
              offset: true,
            },
          },
        },
      })
    )
  }, [canvasRef])

  // Re-fetch data if input changes
  useEffect(() => {
    JSONFetcher.put({
      url: apiUrl,
      body: {
        dimension: dimension,
        period: period,
        filter: activeProjectId,
      },
      success: setResponse,
    })
  }, [dimension, period, activeProjectId, apiUrl])

  // Update URL filter-param with activeProjectId
  useEffect(() => {
    const urlSearch = QueryHelper.toQuery({ filter: activeProjectId })
    // .replace b/c the back button does not refresh the chart (yet)
    createBrowserHistory().replace({ pathname: window.location.pathname, search: urlSearch })
  }, [activeProjectId])

  // Update chart once data is loaded and chart is initialized
  useEffect(() => {
    if (chart && response) {
      // eslint-disable-next-line react-hooks/immutability
      chart.data.labels = response.labels
      chart.data.datasets = response.datasets
      chart.update()
    }
  }, [chart, response])

  // Update label and yAxis if dimension changes
  useEffect(() => {
    if (chart) {
      // eslint-disable-next-line react-hooks/immutability
      chart.options.plugins!.tooltip!.callbacks!.label = (item) => {
        const value =
          dimension === 'count' ? item.raw : formatMoney(i18n.locale, parseFloat((item.raw as string) ?? '0.0'))
        const label = i18n.t(`manage.detailed_chart.tooltip_label_dimension_${dimension}`)
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `${label} : ${value}`
      }
      // FIXME Overwrite initial format to handle `dimension === 'count'`.

      chart.options.scales!.y!.ticks!.callback = (value) => {
        return dimension === 'count'
          ? value
          : formatMoney(i18n.locale, parseFloat(`${value}` || '0.0'), { precision: 0 })
      }
    }
  }, [response, dimension, chart])

  const onSelectionChanged: React.ChangeEventHandler<HTMLSelectElement> = useCallback((e) => {
    setActiveProjectId(e.target.value)
  }, [])

  const onPeriodChanged: React.ChangeEventHandler<HTMLSelectElement> = useCallback((e) => setPeriod(e.target.value), [])

  return (
    <div className="mb-12" style={{ padding: '1.5rem', borderRadius: '3px' }}>
      <div className="flex justify-between flex-col desktop:flex-row">
        <div
          className="flex desktop:mr-2 flex-col desktop:flex-row"
          style={{ flexGrow: 0, flexShrink: 0, flexBasis: '50%' }}
        >
          {filters !== 'only-period' && (
            <div className="desktop:mr-2 flex-1">
              <label>{i18n.t('manage.detailed_chart.filter_label')}</label>
              <select onChange={onSelectionChanged} value={activeProjectId}>
                <option value="">{i18n.t('manage.detailed_chart.filter_all_option')}</option>
                {projects_filter_selection_items.map(({ title, id }) => (
                  <option key={id} value={id}>
                    {decode(title)}
                  </option>
                ))}
                {projects_filter_selection_secondary_items.length && (
                  <>
                    <optgroup label={i18n.t('manage.detailed_chart.filter_group_label')}>
                      {projects_filter_selection_secondary_items.map(({ title, id }) => (
                        <option key={id} value={id}>
                          {decode(title)}
                        </option>
                      ))}
                    </optgroup>
                  </>
                )}
              </select>
            </div>
          )}
          <div className="mt-2 desktop:mt-0 flex-1">
            <label>{i18n.t('manage.detailed_chart.period_label')}</label>
            <select onChange={onPeriodChanged} value={period}>
              <option value="last_12_weeks">{i18n.t('manage.detailed_chart.period_last_12_weeks')}</option>
              <option value="last_12_months">{i18n.t('manage.detailed_chart.period_last_12_months')}</option>
              <option value="">{i18n.t('manage.detailed_chart.period_all_years')}</option>
              {years.reverse().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
        <DimensionToggleButtons dimension={dimension} setDimension={setDimension} />
      </div>
      <div className="chart-container mt-6 w-full" style={{ position: 'relative' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  )
}

const DimensionToggleButtons = ({ dimension, setDimension }: DimensionToggleButtonsProps): JSX.Element => {
  const onDimensionChanged: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => setDimension(e.target.value as Dimension),
    [setDimension]
  )
  return (
    <div className="mt-2 desktop:mt-0">
      <label>{i18n.t('manage.detailed_chart.dimension_label')}</label>
      <div role="group" className="w-full button-group button-group-toggle">
        {Dimensions.map((radio, index) => (
          <label
            className={classNames([
              'btn whitespace-nowrap',
              dimension === radio ? 'btn-primary no-pseudo-hover-styles' : 'btn-secondary',
            ])}
            key={index}
          >
            <input name="radio" type="radio" value={radio} onChange={onDimensionChanged} />
            <span>{i18n.t(`manage.detailed_chart.dimension_option_${radio}`)}</span>
          </label>
        ))}
      </div>
    </div>
  )
}
